import {
    useRef,
    useState,
    useEffect,
    useContext,
    useLayoutEffect,
} from "react";
import { Dialog, DialogType, Stack } from "@fluentui/react";
import { ShieldLockRegular, ErrorCircleRegular } from "@fluentui/react-icons";
import buttonPauseSvg from "../../assets/button_pause.svg";
import { FaLink } from "react-icons/fa6";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import uuid from "react-uuid";
import { isEmpty } from "lodash-es";

import styles from "./Chat.module.css";
import Heading from "../../assets/Heading.png";
import CaritasSpeechBubble from "../../assets/caritas_logo_speech_bubble.svg";

import {
    ChatMessage,
    ConversationRequest,
    conversationApi,
    Citation,
    ToolMessageContent,
    ChatResponse,
    Conversation,
    ErrorMessage,
    CitationWithReference,
} from "../../api";
import { Answer } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { AppStateContext } from "../../state/AppProvider";
import { useBoolean } from "@fluentui/react-hooks";
import { useTexts } from "../../context/TextProvider";
import { HtmlContent } from "../../components/HtmlContent";
import {
    Calculator,
    CalculatorResultCard,
    CalculatorWelcomeCard,
    formatCalculatorReport,
} from "../../components/Calculator";
import type { CalculatorResult } from "../../components/Calculator";
import { getTexts } from "../../components/Calculator/locales";

const enum messageStatus {
    NotRunning = "Not Running",
    Processing = "Processing",
    Done = "Done",
}

const Chat = (props: any) => {
    const appStateContext = useContext(AppStateContext);
    const { texts } = useTexts();
    const chatMessageStream = useRef<HTMLDivElement | null>(null);

    // Scroll only our own window; scrollIntoView would also scroll the parent page when embedded in an iframe.
    const scrollChatToBottom = () => {
        const el = chatMessageStream.current;
        if (!el) return;
        window.scrollTo({ top: window.scrollY + el.getBoundingClientRect().bottom, behavior: "smooth" });
    };
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [hasConsent, setHasConsent] = useState<boolean>(false);
    const [showLoadingMessage, setShowLoadingMessage] =
        useState<boolean>(false);
    const [showUsageDisclaimer, setShowUsageDisclaimer] =
        useState<boolean>(false);
    const [tokenLimitReached, setTokenLimitReached] = useState<boolean>(false);
    const [activeCitation, setActiveCitation] =
        useState<CitationWithReference>();
    const [isCitationPanelOpen, setIsCitationPanelOpen] =
        useState<boolean>(false);
    const abortFuncs = useRef([] as AbortController[]);
    const [showAuthMessage, setShowAuthMessage] = useState<boolean>(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [processMessages, setProcessMessages] = useState<messageStatus>(
        messageStatus.NotRunning
    );
    const [hideErrorDialog, { toggle: toggleErrorDialog }] = useBoolean(true);
    const [errorMsg, setErrorMsg] = useState<ErrorMessage | null>();
    const [sessionId, setSessionId] = useState<string>(uuid());
    const [isCalculatorOpen, setIsCalculatorOpen] = useState<boolean>(false);
    const [isCalculatorWelcomeActive, setIsCalculatorWelcomeActive] = useState<boolean>(false);
    const [calculatorLang, setCalculatorLang] = useState<string | undefined>(undefined);

    const errorDialogContentProps = {
        type: DialogType.close,
        title: errorMsg?.title,
        closeButtonAriaLabel: "Close",
        subText: errorMsg?.subtitle,
    };

    const modalProps = {
        titleAriaId: "labelId",
        subtitleAriaId: "subTextId",
        isBlocking: true,
        styles: { main: { maxWidth: 450 } },
    };

    const [ASSISTANT, TOOL, ERROR] = ["assistant", "tool", "error"];

    const handleErrorDialogClose = () => {
        toggleErrorDialog();
        setTimeout(() => {
            setErrorMsg(null);
        }, 500);
    };

    const openUsageDisclaimer = () => {
        appStateContext?.dispatch({
            type: "UPDATE_SHOW_USAGE_DISCLAIMER",
            payload: true,
        });
        setShowUsageDisclaimer(true);
    };

    const closeUsageDisclaimer = () => {
        appStateContext?.dispatch({
            type: "UPDATE_SHOW_USAGE_DISCLAIMER",
            payload: false,
        });
        setShowUsageDisclaimer(false);
    };

    let assistantMessage = {} as ChatMessage;
    let toolMessage = {} as ChatMessage;
    let assistantContent = "";

    const processResultMessage = (
        resultMessage: ChatMessage,
        userMessage: ChatMessage,
        conversationId?: string
    ) => {
        if (resultMessage.role === ASSISTANT) {
            assistantContent += resultMessage.content;
            assistantMessage = { ...resultMessage, content: assistantContent };
        }

        if (resultMessage.role === TOOL) toolMessage = resultMessage;

        if (!conversationId) {
            isEmpty(toolMessage)
                ? setMessages([...messages, userMessage, assistantMessage])
                : setMessages([
                      ...messages,
                      userMessage,
                      toolMessage,
                      assistantMessage,
                  ]);
        } else {
            isEmpty(toolMessage)
                ? setMessages([...messages, assistantMessage])
                : setMessages([...messages, toolMessage, assistantMessage]);
        }
    };

    const makeApiRequest = async (
        question: string,
        conversationId?: string
    ) => {
        setIsLoading(true);
        setShowLoadingMessage(true);
        const abortController = new AbortController();
        abortFuncs.current.unshift(abortController);

        const userMessage: ChatMessage = {
            id: uuid(),
            role: "user",
            content: question,
            date: new Date().toISOString(),
        };

        let conversation: Conversation | null | undefined;
        if (!conversationId) {
            conversation = {
                id: conversationId ?? uuid(),
                title: question,
                messages: [userMessage],
                date: new Date().toISOString(),
            };
        } else {
            conversation = appStateContext?.state?.currentChat;
            if (!conversation) {
                console.error("Conversation not found.");
                setIsLoading(false);
                setShowLoadingMessage(false);
                abortFuncs.current = abortFuncs.current.filter(
                    (a) => a !== abortController
                );
                return;
            } else {
                conversation.messages.push(userMessage);
            }
        }

        appStateContext?.dispatch({
            type: "UPDATE_CURRENT_CHAT",
            payload: conversation,
        });
        setMessages(conversation.messages);

        const calculatorMessage = conversation.messages.find(
            (m) => m.role === "calculator_result"
        );

        const request: ConversationRequest = {
            messages: [
                ...conversation.messages.filter(
                    (answer) => answer.role !== ERROR
                ),
            ],
            session_id: sessionId,
            ...(calculatorMessage && {
                calculator_report: formatCalculatorReport(
                    JSON.parse(calculatorMessage.content) as CalculatorResult
                ),
            }),
        };

        if (request.calculator_report) {
            console.log("[calculator_report]", request.calculator_report);
        }

        let result = {} as ChatResponse;
        try {
            const response = await conversationApi(
                request,
                abortController.signal
            );
            if (response?.body) {
                const traceId = response.headers.get('x-langfuse-trace-id') ?? undefined;
                const reader = response.body.getReader();
                let runningText = "";

                while (true) {
                    setProcessMessages(messageStatus.Processing);
                    const { done, value } = await reader.read();
                    if (done) break;

                    const text = new TextDecoder("utf-8").decode(value);
                    const objects = text.split("\n");
                    objects.forEach((obj) => {
                        try {
                            runningText += obj;
                            result = JSON.parse(runningText);
                            result.choices[0].messages.forEach((obj) => {
                                obj.id = uuid();
                                obj.date = new Date().toISOString();
                                if (obj.role === 'assistant') {
                                    obj.traceId = traceId;
                                }
                            });
                            setShowLoadingMessage(false);
                            result.choices[0].messages.forEach((resultObj) => {
                                processResultMessage(
                                    resultObj,
                                    userMessage,
                                    conversationId
                                );
                            });
                            runningText = "";
                        } catch {}
                    });
                }
                if (!conversation) return;
                if (!isEmpty(toolMessage)) conversation.messages.push(toolMessage);
                if (!isEmpty(assistantMessage)) conversation.messages.push(assistantMessage);
                appStateContext?.dispatch({
                    type: "UPDATE_CURRENT_CHAT",
                    payload: conversation,
                });
                setMessages([...conversation.messages]);
            }
        } catch (e) {
            if (!abortController.signal.aborted) {
                let errorMessage =
                    "An error occurred. Please try again. If the problem persists, please contact the site administrator.";
                
                // Check if the error is due to token limit being reached
                if (e instanceof Error && e.message === 'TOKEN_LIMIT_REACHED') {
                    setTokenLimitReached(true);
                    errorMessage = texts?.texts?.chat?.error?.tokenLimitReached ?? "Es tut mir leid, aber ich habe im Moment schon so viele Fragen beantwortet, dass ich keine weiteren Antworten geben kann. Bitte versuchen Sie es später noch einmal.";
                } else if (result.error?.message) {
                    errorMessage = result.error.message;
                } else if (typeof result.error === "string") {
                    errorMessage = result.error;
                }
                const errorChatMsg: ChatMessage = {
                    id: uuid(),
                    role: ERROR,
                    content: errorMessage,
                    date: new Date().toISOString(),
                };
                conversation.messages.push(errorChatMsg);
                appStateContext?.dispatch({
                    type: "UPDATE_CURRENT_CHAT",
                    payload: conversation,
                });
                setMessages([...messages, errorChatMsg]);
            } else {
                setMessages([...messages, userMessage]);
            }
        } finally {
            setIsLoading(false);
            setShowLoadingMessage(false);
            abortFuncs.current = abortFuncs.current.filter(
                (a) => a !== abortController
            );
            setProcessMessages(messageStatus.Done);
        }

        return abortController.abort();
    };

    const stopGenerating = () => {
        abortFuncs.current.forEach((a) => a.abort());
        setShowLoadingMessage(false);
        setIsLoading(false);
    };

    useEffect(() => {
        if (appStateContext?.state.currentChat) {
            setMessages(appStateContext.state.currentChat.messages);
        } else {
            setMessages([]);
            setSessionId(uuid());
        }
        setShowUsageDisclaimer(
            appStateContext?.state.showUsageDisclaimer ?? false
        );
    }, [
        appStateContext?.state.currentChat,
        appStateContext?.state.showUsageDisclaimer,
    ]);

    useLayoutEffect(() => {
        if (
            appStateContext &&
            appStateContext.state.currentChat &&
            processMessages === messageStatus.Done
        ) {
            setMessages(appStateContext.state.currentChat.messages);
            setProcessMessages(messageStatus.NotRunning);
        }
    }, [processMessages]);

    useLayoutEffect(() => {
        scrollChatToBottom();
    }, [showLoadingMessage, processMessages, isCalculatorOpen]);

    useEffect(() => {
        const lastToolIndex = messages.reduce((idx, m, i) => (m.role === "tool" ? i : idx), -1);
        if (lastToolIndex < 0) return;
        const lastToolMessage = messages[lastToolIndex];
        if (parseToolMessageContent(lastToolMessage)?.trigger_calculator) {
            const hasResultAfterTool = messages.slice(lastToolIndex).some(m => m.role === "calculator_result");
            if (!hasResultAfterTool) {
                setIsCalculatorWelcomeActive(true);
            }
        }
    }, [messages.length]);

    const onShowCitation = (citation: CitationWithReference) => {
        setActiveCitation(citation);
        setIsCitationPanelOpen(true);
    };

    const onViewSource = (citation: Citation) => {
        if (citation.url && !citation.url.includes("blob.core")) {
            window.open(citation.url, "_blank");
        }
    };

    const parseCitationFromMessage = (message: ChatMessage) => {
        if (message?.role && message?.role === "tool") {
            try {
                const toolMessage = JSON.parse(
                    message.content
                ) as ToolMessageContent;
                return toolMessage.citations;
            } catch {
                return [];
            }
        }
        return [];
    };

    const parseToolMessageContent = (message: ChatMessage | undefined): ToolMessageContent | null => {
        if (message?.role === "tool") {
            try {
                return JSON.parse(message.content) as ToolMessageContent;
            } catch {
                return null;
            }
        }
        return null;
    };

    return (
        <div className={styles.container} role="main">
            {showAuthMessage ? (
                <Stack className={styles.chatEmptyState}>
                    <ShieldLockRegular
                        className={styles.chatIcon}
                        style={{
                            color: "darkorange",
                            height: "200px",
                            width: "200px",
                        }}
                    />
                    <h1 className={styles.chatEmptyStateTitle}>
                        Authentication Not Configured
                    </h1>
                    <h2 className={styles.chatEmptyStateSubtitle}>
                        This app does not have authentication configured. Please
                        add an identity provider by finding your app in the
                        <a href="https://portal.azure.com/" target="_blank" rel="noreferrer">
                            {" "}
                            Azure Portal{" "}
                        </a>
                        and following
                        <a
                            href="https://learn.microsoft.com/en-us/azure/app-service/scenario-secure-app-authentication-app-service#3-configure-authentication-and-authorization"
                            target="_blank" rel="noreferrer"
                        >
                            {" "}
                            these instructions
                        </a>
                        .
                    </h2>
                    <h2
                        className={styles.chatEmptyStateSubtitle}
                        style={{ fontSize: "20px" }}
                    >
                        <strong>
                            Authentication configuration takes a few minutes to
                            apply.{" "}
                        </strong>
                    </h2>
                    <h2
                        className={styles.chatEmptyStateSubtitle}
                        style={{ fontSize: "20px" }}
                    >
                        <strong>
                            If you deployed in the last 10 minutes, please wait
                            and reload the page after 10 minutes.
                        </strong>
                    </h2>
                </Stack>
            ) : (
                <Stack className={styles.chatRoot}>
                    <div className={styles.chatContainer}>
                        {!messages || messages.length < 1 ? (
                            <Stack className={styles.chatEmptyStateTop}>
                                <h1 className={styles.chatEmptyStateTitle}>
                                    <img
                                        src={Heading}
                                        className={styles.heading}
                                        alt="Grundsicherung? Frag erst mich!"
                                    />
                                </h1>
                                <div className={styles.chatEmptySecondTitle}>
                                    {texts?.texts.start.disclaimer1 ? (
                                        <HtmlContent html={texts.texts.start.disclaimer1} />
                                    ) : (
                                        <HtmlContent html="Hier können Sie <strong>kostenlos und anonym</strong> Ihre Fragen zur Grundsicherung stellen." />
                                    )}
                                </div>
                            </Stack>
                        ) : (
                            <>
                                <div
                                    ref={chatMessageStream}
                                    className={styles.chatMessageStream}
                                    style={{
                                        marginBottom: isLoading
                                            ? "40px"
                                            : "0px",
                                        direction: messages.some(m => parseToolMessageContent(m)?.language === "ar") ? "rtl" : undefined,
                                    }}
                                    role="log"
                                >
                                    {messages.map((answer, index) => {
                                        const toolContent = parseToolMessageContent(messages[index - 1]);
                                        return (
                                            <div key={answer.id || index}>
                                            {answer.role === "user" ? (
                                                <div
                                                    key={`u-${index}`}
                                                    className={
                                                        styles.chatMessageUser
                                                    }
                                                    tabIndex={0}
                                                >
                                                    <div
                                                        className={
                                                            styles.chatMessageUserMessage
                                                        }
                                                    >
                                                        {answer.content}
                                                    </div>
                                                </div>
                                            ) : answer.role === "assistant" && toolContent?.trigger_calculator ? (
                                                <div
                                                    key={`calc-welcome-${index}`}
                                                    className={styles.chatMessageGpt}
                                                >
                                                    <img src={CaritasSpeechBubble} className={styles.chatMessageGptIcon} aria-hidden="true" alt="" />
                                                    <CalculatorWelcomeCard
                                                        onStart={() => {
                                                            setIsCalculatorWelcomeActive(false);
                                                            setIsCalculatorOpen(true);
                                                            setCalculatorLang(toolContent.language);
                                                        }}
                                                        onDecline={() => setIsCalculatorWelcomeActive(false)}
                                                        isCalculatorOpen={isCalculatorOpen}
                                                        texts={getTexts(toolContent.language)}
                                                    />
                                                </div>
                                            ) : answer.role === "calculator_result" ? (
                                                <div
                                                    key={`calc-result-${index}`}
                                                    className={styles.chatMessageGpt}
                                                >
                                                    <CalculatorResultCard
                                                        result={JSON.parse(answer.content) as CalculatorResult}
                                                        texts={getTexts(calculatorLang)}
                                                    />
                                                </div>
                                            ) : answer.role === "assistant" ? (
                                                <div
                                                    key={`a-${index}`}
                                                    className={
                                                        styles.chatMessageGpt
                                                    }
                                                >
                                                    <img src={CaritasSpeechBubble} className={styles.chatMessageGptIcon} aria-hidden="true" alt="" />
                                                    <Answer
                                                        answer={{
                                                            answer: answer.content,
                                                            citations:
                                                                parseCitationFromMessage(
                                                                    messages[
                                                                        index -
                                                                            1
                                                                    ]
                                                                ),
                                                        }}
                                                        onCitationClicked={(
                                                            c
                                                        ) => onShowCitation(c)}
                                                        traceId={answer.traceId}
                                                    />
                                                </div>
                                            ) : answer.role === ERROR ? (
                                                <div
                                                    key={`e-${index}`}
                                                    className={
                                                        styles.chatMessageError
                                                    }
                                                >
                                                    <Stack
                                                        horizontal
                                                        className={
                                                            styles.chatMessageErrorContent
                                                        }
                                                    >
                                                        <ErrorCircleRegular
                                                            className={
                                                                styles.errorIcon
                                                            }
                                                        />
                                                        <span></span>
                                                    </Stack>
                                                    <span
                                                        className={
                                                            styles.chatMessageErrorContent
                                                        }
                                                    >
                                                        {answer.content}
                                                    </span>
                                                </div>
                                            ) : null}
                                            </div>
                                        );
                                    })}
                                    {showLoadingMessage && (
                                        <>
                                            <div
                                                className={
                                                    styles.chatMessageGpt
                                                }
                                            >
                                                <img src={CaritasSpeechBubble} className={styles.chatMessageGptIcon} aria-hidden="true" alt="" />
                                                <Answer
                                                    answer={{
                                                        answer: "Antwort wird erzeugt...",
                                                        citations: [],
                                                    }}
                                                    onCitationClicked={() =>
                                                        null
                                                    }
                                                    showActions={false}
                                                />
                                            </div>
                                        </>
                                    )}
                                    {isCalculatorOpen && (
                                        <Calculator
                                            lang={calculatorLang}
                                            onClose={() => setIsCalculatorOpen(false)}
                                            onStepChange={() => requestAnimationFrame(scrollChatToBottom)}
                                            onResult={(result: CalculatorResult) => {
                                                setIsCalculatorOpen(false);
                                                const resultMessage: ChatMessage = {
                                                    id: uuid(),
                                                    role: "calculator_result",
                                                    content: JSON.stringify(result),
                                                    date: new Date().toISOString(),
                                                };
                                                const currentChat = appStateContext?.state.currentChat;
                                                if (currentChat) {
                                                    appStateContext?.dispatch({
                                                        type: "UPDATE_CURRENT_CHAT",
                                                        payload: {
                                                            ...currentChat,
                                                            messages: [...currentChat.messages, resultMessage],
                                                        },
                                                    });
                                                }
                                                setMessages(prev => [...prev, resultMessage]);
                                            }}
                                        />
                                    )}
                                </div>
                            </>
                        )}

                        <Stack horizontal className={`${styles.chatInput}${messages.length > 0 ? ` ${styles.chatInputWide}` : ""}`} style={{ display: isCalculatorWelcomeActive || isCalculatorOpen ? "none" : undefined }}>
                            <Stack>
                                <Dialog
                                    hidden={hideErrorDialog}
                                    onDismiss={handleErrorDialogClose}
                                    dialogContentProps={errorDialogContentProps}
                                    modalProps={modalProps}
                                ></Dialog>
                            </Stack>
                            <QuestionInput
                                clearOnSend
                                placeholder={tokenLimitReached ? texts?.texts?.chat?.placeholderTokenLimitReached ?? "Bitte versuchen Sie es später noch einmal." : texts?.texts?.chat?.placeholder}
                                disabled={isLoading || tokenLimitReached || isCalculatorOpen}
                                onSend={(question, id) => {
                                    makeApiRequest(question, id);
                                }}
                                conversationId={
                                    appStateContext?.state.currentChat?.id
                                        ? appStateContext?.state.currentChat?.id
                                        : undefined
                                }
                                onOpenUsageDisclaimer={openUsageDisclaimer}
                                onConsent={() => {
                                    setHasConsent(true);
                                    appStateContext?.dispatch({ type: "UPDATE_CONSENT_GIVEN", payload: true });
                                }}
                                showEyecatcher={messages.length > 0 ? false : (texts?.texts.start.showEyecatcher ?? false)}
                                hasMessages={messages.length > 0}
                            />
                            {isLoading && (
                                <Stack
                                    horizontal
                                    className={styles.stopGeneratingContainer}
                                    role="button"
                                    aria-label="Generierung stoppen"
                                    tabIndex={0}
                                    onClick={stopGenerating}
                                    onKeyDown={(e) =>
                                        e.key === "Enter" || e.key === " "
                                            ? stopGenerating()
                                            : null
                                    }
                                >
                                    <img
                                        src={buttonPauseSvg}
                                        className={styles.stopGeneratingIcon}
                                        aria-hidden="true"
                                    />
                                </Stack>
                            )}
                        </Stack>

                        {!messages || messages.length < 1 ? (
                            <Stack className={styles.chatEmptyStateBottomContainer}>
                                <Stack className={styles.chatEmptyStateBottom}>
                                    <div className={hasConsent ? styles.disclaimerHidden : undefined}>
                                        <div className={styles.chatEmptyDisclaimer}>
                                            {texts?.texts.start.disclaimer2 ? (
                                                <HtmlContent html={texts.texts.start.disclaimer2} />
                                            ) : (
                                                "Der Caritas-Chatbot leistet keine rechtsverbindliche Beratung."
                                            )}
                                        </div>
                                        <div className={styles.chatEmptyDisclaimer}>
                                            {texts?.texts.start.disclaimer3 ? (
                                                <HtmlContent html={texts.texts.start.disclaimer3} />
                                            ) : (
                                                "Bitte keine persönlichen Daten, wie Namen, Adressen oder Kontaktdaten eingeben." )}
                                        </div>
                                        <div className={styles.chatEmptyDisclaimer}>
                                            {texts?.texts.start.disclaimer4 ? (
                                                <HtmlContent html={texts.texts.start.disclaimer4} />
                                            ) : (
                                                "Mit dem Start des Chats erklären Sie sich einverstanden, dass freiwillig eingegebene sensible Daten zur Qualitätskontrolle verarbeitet werden dürfen." )}
                                        </div>
                                        <div className={`${styles.chatEmptyDisclaimer} ${styles.disclaimerLinks}`}>
                                            <a href="#" onClick={() => openUsageDisclaimer()} className={styles.disclaimerBtn}>
                                                {texts?.texts.navigation.usageConsent ?? "Nutzungsbedingungen"}
                                            </a>
                                            |
                                            <a href={texts?.texts.navigation.dataPrivacyUrl ?? "http://www.das-steht-dir-zu.de/impressum/datenschutz"} target="_blank" rel="noopener noreferrer">
                                                {texts?.texts.navigation.dataPrivacy ?? "Datenschutz"}
                                            </a>
                                            |
                                            <a href={texts?.texts.navigation.legalNoticeUrl ?? "http://www.das-steht-dir-zu.de/impressum"} target="_blank" rel="noopener noreferrer">
                                                {texts?.texts.navigation.legalNotice ?? "Impressum"}
                                            </a>
                                        </div>
                                    </div>
                                </Stack>
                                <Stack className={styles.chatEmptyStateBottomMobile}>
                                    <div className={hasConsent ? styles.disclaimerHidden : undefined}>
                                        <div className={styles.chatEmptyDisclaimer}>
                                            {texts?.texts.start.disclaimer2mobile ? (
                                                <HtmlContent html={texts.texts.start.disclaimer2mobile} />
                                            ) : (
                                                "Der Chatbot kann Fehler machen."
                                            )}
                                        </div>
                                        <div className={styles.chatEmptyDisclaimer}>
                                            {texts?.texts.start.disclaimer3mobile ? (
                                                <HtmlContent html={texts.texts.start.disclaimer3mobile} />
                                            ) : (
                                                "Keine rechtsverbindliche Beratung." )}
                                        </div>
                                        <div className={styles.chatEmptyDisclaimer}>
                                            {texts?.texts.start.disclaimer4mobile ? (
                                                <HtmlContent html={texts.texts.start.disclaimer4mobile} />
                                            ) : (
                                                "Keine persönlichen Daten eingeben." )}
                                        </div>
                                        <div className={styles.chatEmptyDisclaimer}>
                                            {texts?.texts.start.disclaimer5mobile ? (
                                                <HtmlContent html={texts.texts.start.disclaimer5mobile} />
                                            ) : (
                                                "Eingaben können zur Qualitätskontrolle verarbeitet werden." )}
                                        </div>
                                        <div className={`${styles.chatEmptyDisclaimer} ${styles.disclaimerLinks}`}>
                                            <a href="#" onClick={() => openUsageDisclaimer()} className={styles.disclaimerBtn}>
                                                {texts?.texts.navigation.usageConsent ?? "Nutzungsbedingungen"}
                                            </a>
                                            |
                                            <a href={texts?.texts.navigation.dataPrivacyUrl ?? "http://www.das-steht-dir-zu.de/impressum/datenschutz"} target="_blank" rel="noopener noreferrer">
                                                {texts?.texts.navigation.dataPrivacy ?? "Datenschutz"}
                                            </a>
                                            |
                                            <a href={texts?.texts.navigation.legalNoticeUrl ?? "http://www.das-steht-dir-zu.de/impressum"} target="_blank" rel="noopener noreferrer">
                                                {texts?.texts.navigation.legalNotice ?? "Impressum"}
                                            </a>
                                        </div>
                                    </div>
                                </Stack>
                            </Stack>
                        ) : null }
                    </div>
                    {/* Citation Panel */}
                    {messages &&
                        messages.length > 0 &&
                        isCitationPanelOpen &&
                        activeCitation && (
                            <Stack.Item
                                className={styles.citationPanel}
                                tabIndex={0}
                                role="region"
                                aria-label="Zitatpanel"
                            >
                                <Stack
                                    horizontal
                                    className={
                                        styles.citationPanelHeaderContainer
                                    }
                                    horizontalAlign="space-between"
                                    verticalAlign="center"
                                >
                                    <span
                                        className={styles.citationPanelHeader}
                                    >
                                        Zitat
                                    </span>

                                    <button
                                        className={styles.citationPanelClose}
                                        aria-label="Fenster schließen"
                                        onClick={() => setIsCitationPanelOpen(false)}
                                    />
                                </Stack>
                                <div
                                    className={
                                        styles.citationReferenceContainer
                                    }
                                >
                                    <span
                                        className={
                                            styles.citationReferenceTitleNumber
                                        }
                                    >
                                        {activeCitation.referenceTitle.substring(
                                            0,
                                            activeCitation.referenceTitle.indexOf(
                                                " "
                                            )
                                        )}
                                    </span>
                                    <span
                                        className={
                                            styles.citationReferenceTitle
                                        }
                                    >
                                        {activeCitation.referenceTitle.substring(
                                            activeCitation.referenceTitle.indexOf(
                                                " "
                                            )
                                        )}
                                    </span>
                                </div>
                                {activeCitation.url && (
                                    <h5
                                        className={styles.citationPanelTitle}
                                        tabIndex={0}
                                        title={
                                            activeCitation.url &&
                                            !activeCitation.url.includes(
                                                "blob.core"
                                            )
                                                ? activeCitation.url
                                                : activeCitation.title ?? ""
                                        }
                                        onClick={() =>
                                            onViewSource(activeCitation)
                                        }
                                    >
                                        {FaLink({})} {activeCitation.url}
                                    </h5>
                                )}
                                <div tabIndex={0}>
                                    <ReactMarkdown
                                        linkTarget="_blank"
                                        className={styles.citationPanelContent}
                                        children={activeCitation.content}
                                        remarkPlugins={[remarkGfm]}
                                        rehypePlugins={[rehypeRaw]}
                                    />
                                </div>
                            </Stack.Item>
                        )}
                    {/* Nutzungsbedingungen Panel */}
                    {showUsageDisclaimer && (
                        <div className={styles.usageDisclaimerContainer}>
                            <div className={styles.usageDisclaimerHeader}>
                            <div className={styles.usageDisclaimerTitle}>
                                    {texts?.texts.usageDisclaimer.title ?? "Nutzungsbedingungen für den KI-Chatbot"}
                                </div>
                                <div>
                                    <button
                                        className={styles.citationPanelClose}
                                        aria-label="Fenster schließen"
                                        onClick={() => closeUsageDisclaimer()}
                                    />
                                </div>
                            </div>
                            <div className={styles.usageDisclaimerBody}>
                                <div className={styles.usageDisclaimerHeadline}>
                                    {texts?.texts.usageDisclaimer.section1.title ?? "1. Zweck und Funktionsweise"}
                                </div>
                                <div>
                                    {texts?.texts.usageDisclaimer.section1.content ? (
                                        <HtmlContent html={texts.texts.usageDisclaimer.section1.content} />
                                    ) : (
                                        "Der KI-Chatbot dient ausschließlich der Erstinformation zu möglichen Ansprüchen auf Grundsicherung. Die Beratung erfolgt auf Basis der eingegebenen Informationen und ersetzt keine persönliche Beratung. Bei der Nutzung des KI-Chatbots haben Sie die Möglichkeit, besondere Kategorien personenbezogener Daten im Sinne von § 4 Nr. 2 KDG einzugeben (z. B. Angabe über das Bestehen einer Schwangerschaft). Mit der Eingabe der Daten erteilen Sie uns die Erlaubnis, diese Daten zu erheben und zu speichern, um Ihnen die Prüfung Ihrer Ansprüche zu ermöglichen."
                                    )}
                                </div>
                                <div className={styles.usageDisclaimerHeadline}>
                                    {texts?.texts.usageDisclaimer.section2.title ?? "2. Anonymität und Datenschutz"}
                                </div>
                                <div>
                                    <ul>
                                        {texts?.texts.usageDisclaimer.section2.content ? (
                                            texts.texts.usageDisclaimer.section2.content.map((item, index) => (
                                                <li key={index}>{item}</li>
                                            ))
                                        ) : (
                                            <>
                                                <li>
                                                    Alle Eingaben im KI-Chatbot werden
                                                    anonymisiert verarbeitet. Es werden
                                                    keine personenbezogenen Daten
                                                    gespeichert oder an Dritte
                                                    weitergegeben.
                                                </li>
                                                <li>
                                                    Nutzer werden gebeten, keine
                                                    personenbezogenen Daten, wie Namen,
                                                    Adressen oder Kontaktdaten, im
                                                    KI-Chatbot einzugeben.
                                                </li>
                                            </>
                                        )}
                                    </ul>
                                </div>
                                <div className={styles.usageDisclaimerHeadline}>
                                    {texts?.texts.usageDisclaimer.section3.title ?? "3. Verzicht auf personenbezogene Eingaben"}
                                </div>
                                <div>
                                    {texts?.texts.usageDisclaimer.section3.content ? (
                                        <HtmlContent html={texts.texts.usageDisclaimer.section3.content} />
                                    ) : (
                                        "Bitte geben Sie keine Informationen ein, die Rückschlüsse auf Ihre Identität oder die Identität Dritter ermöglichen. Dies dient sowohl Ihrem Schutz als auch dem Ziel, die Datensicherheit zu gewährleisten."
                                    )}
                                </div>
                                <div className={styles.usageDisclaimerHeadline}>
                                    {texts?.texts.usageDisclaimer.section4.title ?? "4. Zustimmung zu den Nutzungsbedingungen"}
                                </div>
                                <div>
                                    {texts?.texts.usageDisclaimer.section4.content ? (
                                        <HtmlContent html={texts.texts.usageDisclaimer.section4.content} />
                                    ) : (
                                        "Bitte beachten Sie, dass die Nutzung des KI-Chatbots nur mit vollständiger Akzeptanz der Nutzungsbedingungen möglich ist. Mit der Nutzung des KI-Chatbots wird diese Zustimmung bestätigt."
                                    )}
                                </div>
                                <div className={styles.usageDisclaimerHeadline}>
                                    {texts?.texts.usageDisclaimer.section5.title ?? "5. Verweis auf Datenschutzbestimmungen"}
                                </div>
                                <div>
                                    {texts?.texts.usageDisclaimer.section5.content ? (
                                        <HtmlContent html={texts.texts.usageDisclaimer.section5.content} />
                                    ) : (
                                        "Für weiterführende Informationen zum Datenschutz besuchen Sie bitte die Unterseite"
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                </Stack>
            )}
        </div>
    );
};

export default Chat;
